import { useEffect, useMemo, useState } from 'react';
import { api, ApiError } from './api';

/**
 * The language catalogue, and the one rule that goes with it:
 *
 *   'lessons_live'  lessons and games exist (Yorùbá, and only Yorùbá)
 *   'stories_only'  record, keep and translate your family's stories today; lessons aren't written yet
 *
 * The Story Vault, Voice Tree and Grandparent Calls never needed a curriculum, so every listed
 * language works for them right now. Nothing in the UI may blur that line.
 */
export type LanguageStatus = 'lessons_live' | 'stories_only' | 'planned';

export interface Language {
  code: string;
  name: string;
  native_name: string;
  status: LanguageStatus;
  rtl: boolean;
  speakers_note: string | null;
}

export interface LanguageRegion {
  region: string;
  languages: Language[];
}

export interface Catalogue {
  regions: LanguageRegion[];
  total: number;
  lessonsLive: string[];
}

export interface FamilyLanguage extends Language {
  is_primary: boolean;
  region: string | null;
}

// ---------------------------------------------------------------------------- right-to-left
/** Seeded from the catalogue (db/migrations/006) and confirmed by it once loaded. */
const RTL = new Set(['ar', 'fa', 'he', 'ur']);

export function isRtl(code: string | null | undefined): boolean {
  return !!code && RTL.has(code.toLowerCase().split(/[-_]/)[0]!);
}

/**
 * Spread onto the element that holds text in a given language:
 *   <p {...textProps(story.language_code)}>{story.transcript}</p>
 * Only that text flips — the app's own chrome stays left-to-right.
 */
export function textProps(code: string | null | undefined): { lang?: string; dir?: 'rtl' | 'ltr' } {
  if (!code) return {};
  return { lang: code, dir: isRtl(code) ? 'rtl' : 'ltr' };
}

// ---------------------------------------------------------------------------- the catalogue
/** Public, cacheable reference data: fetch it once per page load and share it. */
let pending: Promise<Catalogue> | null = null;

export function useLanguageCatalogue(): { catalogue: Catalogue | null; byCode: Map<string, Language>; loading: boolean; error: string | null } {
  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    pending ??= api<Catalogue>('/languages');
    pending
      .then((c) => {
        for (const region of c.regions) for (const l of region.languages) if (l.rtl) RTL.add(l.code);
        if (alive) setCatalogue(c);
      })
      .catch((e) => {
        pending = null;
        if (alive) setError(e instanceof ApiError ? e.message : 'Could not load the language list.');
      });
    return () => {
      alive = false;
    };
  }, []);

  const byCode = useMemo(() => {
    const m = new Map<string, Language>();
    for (const region of catalogue?.regions ?? []) for (const l of region.languages) m.set(l.code, l);
    return m;
  }, [catalogue]);

  return { catalogue, byCode, loading: !catalogue && !error, error };
}

/** "Yorùbá", "Igbo (Asụsụ Igbo)" — English name first, the language's own name beside it. */
export function languageLabel(l: Language): string {
  return l.native_name && l.native_name !== l.name ? `${l.name} · ${l.native_name}` : l.name;
}

export function honestLine(l: Language): string {
  return l.status === 'lessons_live'
    ? `Lessons, games and stories are all here in ${l.name}.`
    : `You can record, keep and translate your family's stories in ${l.name} today. Lessons and games are written for Yorùbá first.`;
}
